import * as React from "react";
import Api from "./Api";
import "./AttachmentsDlg.scss";
import LabelCtl from "./input/LabelCtl";
import ModalCtl from "./input/ModalCtl";
import FileCtl from "./input/FileCtl";
import PlainDate from "@testout/testout-commerce/models/PlainDate";
import { Document } from "@testout/testout-commerce/models/commerce/Document";

interface AttachmentsProps {
    orderNumber?: string;
    quoteNumber?: string;
    onClose: () => void;
}

interface AttachmentsState {
    error: string;
    documents: Document[];
    addDocumentPopup: boolean;
    addDocument: Document;
    inProgress: boolean;
    showModal: boolean;
    deleteId: string;
}

export default class AttachmentsDlg extends React.PureComponent<AttachmentsProps, AttachmentsState> {
    constructor(props: AttachmentsProps) {
        super(props);
        this.state = {
            error: "", documents: new Array<Document>(), addDocumentPopup: false, addDocument: new Document(),
            inProgress: true, showModal: false, deleteId: ""
        };
    }

    async componentDidMount() {
        // Load the documents for this order or quote
        try {
            if (this.props.quoteNumber) {
                const documents = await Api.get<Document[]>(`/api/quotes/${this.props.quoteNumber}/attachments`);
                this.setState({ documents: documents });
            } else if (this.props.orderNumber) {
                const documents = await Api.get<Document[]>(`/api/orders/${this.props.orderNumber}/attachments`);
                this.setState({ documents: documents });
            } else
                this.setState({ error: "Unable to find an order ID or a quote ID" });
        }
        catch (error: any) {
            this.setState({ error: `Error while loading the attachments: ${error.message}` });
        }
        this.setState({ inProgress: false });
    }

    async onAddDocumentClick() {
        try {
            this.setState({ error: "", inProgress: true });

            // Rebuild the array so we can set the state of the document array with this new document in it
            let newDocArray = new Array<Document>();
            for (var doc of this.state.documents)
                newDocArray.push(doc);

            // Save this attachment via Commerce Server
            if (this.props.quoteNumber) {
                let savedDoc = await Api.post<Document>(`/api/quotes/${this.props.quoteNumber}/attachments`, this.state.addDocument);
                newDocArray.push(savedDoc);
            } else {
                let savedDoc = await Api.post<Document>(`/api/orders/${this.props.orderNumber}/attachments`, this.state.addDocument);
                newDocArray.push(savedDoc);
            }

            this.setState({ documents: newDocArray });
        }
        catch (error: any) {
            this.setState({ error: `Error while saving the new attachment: ${error.message}` });
        }

        this.setState({ addDocumentPopup: false, inProgress: false });
    }

    onClose = () => {
        this.props.onClose();
    }

    downloadDoc = async (doc: Document) => {
        this.setState({ error: "", inProgress: true });
        if (doc.contentType) {
            if (doc.contentType.includes('outlook')) {
                if (this.props.quoteNumber)
                    window.open('/showMsgFile?quoteDocumentId=' + doc.documentId);
                else
                    window.open('/showMsgFile?orderDocumentId=' + doc.documentId);
                this.setState({ inProgress: false });
                return;
            }
            if (this.props.quoteNumber)
                doc = await Api.get<Document>(`/api/quotes/attachment/${doc.documentId}`);
            else
                doc = await Api.get<Document>(`/api/orders/attachment/${doc.documentId}`);
            doc = new Document(doc);
            if (doc && doc.fileContent) {
                const fileURL = URL.createObjectURL(doc.fileContent);
                window.open(fileURL);
                this.setState({ inProgress: false });
                return;
            }
        }
        this.setState({error: `Unable to retrieve document ${doc.name}. Please submit a service desk ticket.`, inProgress: false});
    }

    // Show the modal that allows the user to delete the attachment
    showDeleteModal = (document: Document) => {
        this.setState({
            deleteId: document.documentId?.toString() ?? "",
            showModal: true,
        });
    }

    // Do the requested action of deleting the attachment
    deleteAttachment = async () => {
        this.setState({ error: "", inProgress: true });
        try {
            // Delete the attachment
            if (this.props.quoteNumber) {
                await Api.delete(`/api/quotes/${this.props.quoteNumber}/attachments/${this.state.deleteId}`);
            } else {
                await Api.delete(`/api/orders/${this.props.orderNumber}/attachments/${this.state.deleteId}`);
            }

            // Remove it from the attachments documents list
            let newDocArray = new Array<Document>();
            for (var doc of this.state.documents)
                if ((doc?.documentId?.toString() ?? "") !== this.state.deleteId)
                    newDocArray.push(doc);
            this.setState({ documents: newDocArray });
        }
        catch (error: any) {
            this.setState({ error: `Error while deleting the attachment: ${error.message}` });
        }
        this.setState({ inProgress: false, showModal: false });
    }

    onFileChange = async (value?: File) => {
        if (value) {
            var document = new Document();
            let type = value.type;
            // If we don't have a type (for the .msg files), create one
            if (value.type === "" && value.name.indexOf('.msg') !== -1)
                type = "application/outlook";
            document.fileContent = new File([value], value.name, { type: type });
            document.createdOn = new Date(value.lastModified);
            this.setState({ addDocument: document });
        }
    }


    render() {
        let label = (this.props.quoteNumber) ? 'Quote Attachments' : 'Order Attachments';
        return <div className="modalScreenBackground">
            <div className="attachmentsDialog">
                <div className="modalHeader">
                    <LabelCtl className="modalHeaderTitle" label={label} />
                    <button className="link" onClick={this.props.onClose}>
                        <div className="modalCloseButton">
                            <svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.5,1.5l-13,13m0-13,13,13" transform="translate(-1 -1)" stroke="#ffffff" strokeWidth="3" />
                            </svg>
                        </div>
                    </button>
                </div>
                {this.state.showModal &&
                    <ModalCtl headerText="Delete this attachment?" onSubmit={this.deleteAttachment} onCancel={() => this.setState({ showModal: false })}
                        submitButton="Delete" saveInProgress={this.state.inProgress}
                        description="To delete this attachment, click the Delete button. This action is permananent." />
                }
                {this.state.inProgress && <div className="inProgress"><div className="spinner"></div></div>}
                {this.state.addDocumentPopup &&
                    <div className="modalScreenBackground">
                        <div className="modalDialog">
                            <div className="modalHeader">
                                <LabelCtl className="modalHeaderTitle" label="Add Attachment" />
                                <button className="link" onClick={() => this.setState({ addDocumentPopup: false })}>
                                    <div className="modalCloseButton">
                                        <svg viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M14.5,1.5l-13,13m0-13,13,13" transform="translate(-1 -1)" stroke="#ffffff" strokeWidth="3" />
                                        </svg>
                                    </div>
                                </button>
                            </div>
                            {this.state.inProgress && <div className="inProgress"><div className="spinner"></div></div>}
                            <div className="attachmentsFileCtl">
                                <LabelCtl label="Click the Upload icon to select a file">
                                    <FileCtl placeholder="Select a file..." accept=".jpg,.png,.msg,.eml,text/csv,application/pdf,.docx,.xlsx," value={this.state.addDocument?.fileContent} onChange={this.onFileChange} />
                                </LabelCtl>
                            </div>
                            <div className="modalBottomButtons">
                                <button className="secondary" onClick={() => this.setState({ addDocumentPopup: false })}>Cancel</button>
                                <button className="primary" onClick={() => this.onAddDocumentClick()}>Add Attachment</button>
                            </div>
                        </div>
                    </div>
                }

                <h3>Attachments</h3>
                <table className="attachmentsTable">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>File Created</th>
                            <th>View</th>
                            <th>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.documents.map((doc: Document) =>
                            <tr>
                                <td>{doc.name}</td>
                                <td>{doc.contentType?.includes('pdf') ? "PDF" :
                                     doc.contentType?.includes('csv') ? "CSV" : 
                                     doc.contentType?.includes('outlook') ? "Email" :
                                     doc.contentType?.includes('wordprocessing') ? "Word" :
                                     doc.contentType?.includes('spreadsheet') ? "Excel" :
                                     doc.contentType?.includes('html') ? "email" : "Text"}</td>
                                <td>{doc.createdOn ? PlainDate.format(new Date(doc.createdOn.toLocaleString()), "MM/dd/yyyy h:mm tt") : ""}</td>
                                <td><button className="followLink" onClick={() => this.downloadDoc(doc)}>&nbsp;</button></td>
                                <td><button className="trash" onClick={() => this.showDeleteModal(doc)}>&nbsp;</button></td>
                            </tr>)
                        }
                    </tbody>
                </table>
                {this.state.error && <div>{this.state.error}</div>}
                <footer className="attachmentsFooter">
                    <button className="secondary" onClick={() => this.setState({ addDocumentPopup: true })}>Add Document</button>
                    <button className="primary" onClick={this.onClose}>Close</button>
                </footer>
            </div>
        </div>;
    }
}